import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import { ChangeEvent, useMemo, useState } from 'react';
import { Alert, Col, Placeholder, Row } from 'react-bootstrap';
import { faFileExport, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from 'react-query';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import { employeesTableColumns } from 'components/tables/EmployeesTable';
import { EmployeeViewBreadcrumbItems } from 'data/commonData';
import { Link } from 'react-router-dom';
import GTable from 'components/tables/GTable';
import { Employee } from 'data/users';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSIONS } from 'constants/permissions';

const ViewEmployee = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const {
    isLoading,
    isError,
    data: employees
  } = useQuery('get_employee', GET_EMPLOYEES_QUERY, {
    staleTime: 3 * 60 * 60 * 1000
  });

  const handleExport = () => {
    if (!employees?.data) return;

    const exportData = employees.data.map(employee => ({
      FirstName: employee.firstName,
      LastName: employee.lastName,
      Email: employee.email,
      PhoneNumber: employee.phoneNumber || '-',
      Username: employee.username,
      Verified: employee.verified ? 'Yes' : 'No',
      Roles: employee.roles?.join(', ') || '-',
      CreatedAt: new Date(employee.createdAt || '').toLocaleDateString(),
      UpdatedAt: new Date(employee.updatedAt || '').toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');

    XLSX.writeFile(wb, 'employees.xlsx');

    toast.success('File has been downloaded successfully');
  };

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = useMemo(() => {
    if (!employees?.data) return [];
    return (
      employees.data
        .filter(
          (employee: Employee) =>
            employee.firstName
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            employee.lastName
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.phoneNumber
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        )
        .sort((a: any, b: any) => {
          return (
            a?.firstName?.localeCompare(b?.firstName ?? '') ||
            a?.lastName?.localeCompare(b?.lastName ?? '') ||
            a?.email?.localeCompare(b?.email ?? '') ||
            a?.phoneNumber?.localeCompare(b?.phoneNumber ?? '')
          );
        }) ?? []
    );
  }, [employees?.data, searchTerm]);

  const [canCreate, canEdit, canDelete] = usePermissions([
    PERMISSIONS.EMPLOYEE.CREATE,
    PERMISSIONS.EMPLOYEE.UPDATE,
    PERMISSIONS.EMPLOYEE.DELETE
  ]);

  const columns = useMemo(
    () => employeesTableColumns(canEdit, canDelete),
    [canEdit, canDelete]
  );

  return (
    <div>
      <PageBreadcrumb items={EmployeeViewBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">Employees</h2>
        {isError && (
          <Alert variant="danger">
            Error fetching employees. Please try again later.
          </Alert>
        )}
        <div className="mb-4">
          <Row className="g-3">
            <Col xs="auto">
              <SearchBox
                placeholder="Search members"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
            </Col>
            <Col
              xs="auto"
              className="scrollbar overflow-hidden-y flex-grow-1"
            ></Col>
            <Col xs="auto">
              <Button
                variant="link"
                className="text-900 me-4 px-0"
                onClick={handleExport}
              >
                <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                Export
              </Button>
              {canCreate && (
                <Link to="/home/apps/employee/create-employee">
                  <Button variant="primary">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Employee
                  </Button>
                </Link>
              )}
            </Col>
          </Row>
        </div>

        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
          {isLoading ? (
            <div className="g-3 mt-8 mb-8">
              <Placeholder as="p" animation="glow">
                <Placeholder xs={12} />
              </Placeholder>
              <Placeholder as="p" animation="glow">
                <Placeholder xs={12} />
              </Placeholder>
            </div>
          ) : (
            <GTable
              data={filteredData || []}
              columns={columns}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewEmployee;
